// import constants
import { BLUE, ORANGE, PURPLE } from 'constants/colors';

// import types
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';
import type { PlanningEntityType } from 'generated/index';
import type {
  BacklogRequiredFilter,
  BacklogTaskCategory
} from './Backlog.types';

/**
 * The entity types a planner can narrow the takenlijst by, in the order the filter popover lays them
 * out: workplace pairs with the activity on the first row, employee with the care receiver on the
 * second, resource alone on the third. Each gets its own search field, and the backend resolves any of
 * them into the care receivers on screen.
 *
 * `as const` rather than a plain `PlanningEntityType[]`: the filter form keys a field per type off this
 * list, which needs the literal union rather than all nine planning entity types.
 */
export const BACKLOG_ENTITY_FILTER_TYPES = [
  'WORKPLACE',
  'EMPLOYEE',
  'CLIENT',
  'RESOURCE'
] as const satisfies readonly PlanningEntityType[];

/**
 * The row types the takenlijst can be shown for, in toolbar order. Exactly one is active: this is a single
 * choice, not a set of filters, because a row can only be one entity.
 *
 * `isSupported` is false while the symbol is not meant to be selectable yet. Resource and workplace rows
 * arrive with #2462, so those two stay disabled — shown, so a planner can see what is coming, but not
 * pickable. Flip them to true once #2462 is in.
 *
 * Employee rows are built from the overlap tile alone: `BacklogDelegate` scopes that view to the picked
 * plan unit's employees, and a prescription cannot describe an employee, so the two prescription tiles are
 * greyed out there — see `entityTypes` below.
 */
export const BACKLOG_ROW_ENTITY_TYPES = [
  { type: 'CLIENT', isSupported: true },
  { type: 'EMPLOYEE', isSupported: true },
  { type: 'WORKPLACE', isSupported: false },
  { type: 'RESOURCE', isSupported: false }
] as const satisfies readonly {
  type: PlanningEntityType;
  isSupported: boolean;
}[];

export type BacklogCategoryMeta = {
  category: BacklogTaskCategory;
  iconType: IconType;
  /**
   * Colour of the tile's symbol when the tile is active, and of the accent line in front of that
   * category's appointment cards, so a card reads back to its tile at a glance. Deliberately not the
   * status colours (red/green/yellow/orange) — those mean shortage/ok/warning — with one exception:
   * tekort/overschot is genuinely a shortage, so it keeps orange. Left unset for the categories that
   * have no backend yet; their tiles are greyed out, so no colour shows.
   */
  iconColor?: string;
  /**
   * False while the category has no backend behind it. Those tiles are still shown — the planner
   * should be able to see what is coming — but they are disabled and carry no count, because a
   * number there would be made up.
   */
  isImplemented: boolean;
  /**
   * The row types this category can describe. A prescription belongs to a care receiver, so deviation
   * and outside-treatment are CLIENT-only; an overlap is keyed on whichever entity the clashing
   * appointments share, so it applies to every row type. A tile that cannot apply to the row on screen
   * is shown disabled rather than hidden — the planner should see the same tiles wherever they are.
   */
  entityTypes: readonly PlanningEntityType[];
};

/** The row types a category applies to when it is not tied to one entity in particular. */
const EVERY_ROW_ENTITY_TYPE = [
  'CLIENT',
  'EMPLOYEE',
  'WORKPLACE',
  'RESOURCE'
] as const satisfies readonly PlanningEntityType[];

/** Prescriptions belong to care receivers, so the tiles read off them describe nothing else. */
const CARE_RECEIVER_ROWS_ONLY = [
  'CLIENT'
] as const satisfies readonly PlanningEntityType[];

/** Order follows the design. Only the first category is backed by data today. */
export const BACKLOG_CATEGORIES: BacklogCategoryMeta[] = [
  {
    category: 'deviation',
    iconType: 'arrow-down-arrow-up',
    iconColor: ORANGE,
    isImplemented: true,
    entityTypes: CARE_RECEIVER_ROWS_ONLY
  },
  {
    category: 'appointmentOverlap',
    iconType: 'layer-group',
    iconColor: PURPLE,
    isImplemented: true,
    entityTypes: EVERY_ROW_ENTITY_TYPE
  },
  {
    category: 'functionMismatch',
    iconType: 'shield-check',
    isImplemented: false,
    entityTypes: EVERY_ROW_ENTITY_TYPE
  },
  { category: 'resourceMismatch', iconType: 'gear', isImplemented: false, entityTypes: EVERY_ROW_ENTITY_TYPE },
  {
    category: 'schedulingGap',
    iconType: 'calendar-xmark',
    isImplemented: false,
    entityTypes: EVERY_ROW_ENTITY_TYPE
  },
  { category: 'absenceConflict', iconType: 'user-xmark', isImplemented: false, entityTypes: EVERY_ROW_ENTITY_TYPE },
  { category: 'outsideWorkingHours', iconType: 'clock', isImplemented: false, entityTypes: EVERY_ROW_ENTITY_TYPE },
  // user-slash reads as "no employee" where user-minus would read as "remove one"; user-xmark is
  // already the absence tile.
  { category: 'missingEmployee', iconType: 'user-slash', isImplemented: false, entityTypes: EVERY_ROW_ENTITY_TYPE },
  // The treatment itself, as a care plan. Deliberately not heart-pulse-xmark, which is available and
  // fits "outside care" but reads as a patient dying in an application like this one.
  {
    category: 'outsideTreatment',
    iconType: 'clipboard-list',
    iconColor: BLUE,
    isImplemented: true,
    entityTypes: CARE_RECEIVER_ROWS_ONLY
  },
  // Care receivers whose prescription is marked SPOED (urgent). Not backed yet, so it shows disabled
  // and countless until its backend lands.
  {
    category: 'spoed',
    iconType: 'triangle-exclamation',
    isImplemented: false,
    entityTypes: EVERY_ROW_ENTITY_TYPE
  },
  { category: 'syncIssue', iconType: 'arrows-rotate', isImplemented: false, entityTypes: EVERY_ROW_ENTITY_TYPE }
];

export const IMPLEMENTED_CATEGORIES: BacklogTaskCategory[] =
  BACKLOG_CATEGORIES.filter(({ isImplemented }) => isImplemented).map(
    ({ category }) => category
  );

/** The colour a category's tile symbol and its appointment-card accent share; undefined if it has none. */
export const backlogCategoryColor = (
  category: BacklogTaskCategory
): string | undefined =>
  BACKLOG_CATEGORIES.find((meta) => meta.category === category)?.iconColor;

export const ALL_TILE_ICON: IconType = 'grid-list';

/**
 * Categories whose rows start folded in. Tekort/Overschot can carry many deviating rules per care
 * receiver, so it opens on demand rather than filling the grid on first load; every other category
 * starts open.
 */
export const CATEGORIES_FOLDED_BY_DEFAULT: BacklogTaskCategory[] = [
  'deviation'
];

/**
 * localStorage key under which the takenlijst remembers its filter and view-settings popovers across
 * visits. Bump the `version` in Backlog.stores.ts if the persisted shape ever changes.
 */
export const BACKLOG_PREFERENCES_STORAGE_KEY = 'roster.backlogPreferences';

/**
 * How long the tile selection settles before it reaches the backend. Tiles stay instant on screen — the
 * client-side row filter is not debounced — but a planner clicking through several categories should cost
 * one request, not one per click, and no request for a half-finished selection.
 */
export const BACKLOG_CATEGORY_DEBOUNCE_MS = 400;

/**
 * How long a single pick's name may be before a filter field reports it as a count instead.
 *
 * The fields sit two per row in a 540px popover, so a name much past this cannot be read anyway — and a
 * truncated name tells a planner less than "1 geselecteerd" does. Measured in characters rather than
 * pixels on purpose: the columns have a fixed width here, so a threshold is predictable and testable
 * where measuring the rendered text would need an observer and could not be tested at all.
 */
export const BACKLOG_FILTER_SUMMARY_MAX_CHARS = 24;

/**
 * What the page says when the view it is on has no filter to work with yet. Keyed by the filter that is
 * missing, so the message names the thing to pick rather than stating that something is missing.
 *
 * A map rather than a key built from the filter name: a composed key cannot be found by the translation
 * tooling, and a missing message would surface as a raw key on an otherwise empty page.
 */
export const BACKLOG_REQUIRED_FILTER_MESSAGES: Record<
  BacklogRequiredFilter,
  string
> = {
  careReceiver: 'filters.careReceiverRequired',
  planUnit: 'filters.planUnitRequired'
};

/**
 * The scheduling label the activity list is asked for. The endpoint returns nothing at all when no label is
 * given, and MRP is the one that yields the planning activities the takenlijst's prescriptions use — the
 * same set the entity search asks for.
 */
export const BACKLOG_ACTIVITY_SCHEDULING_LABEL = 'MRP';
